import { useMemo, useContext } from 'react'
import { SearchTypes } from '../providers/SearchProfilesPage/types'
import { AppDataContext } from '../providers/AppData'
import useTranslateMessage from './useTranslateMessage'
import { useWpaTenant } from './useWpaTenant'
import { getNewPracticesName, newSearchTypesPractices } from '../constants/global'

export default function useSearchMeta(
  location: string,
  searchKeywordTitle: string,
  page = 0,
  isNoindex: boolean = false,
  searchType?: SearchTypes
) {
  const { language, locale, asPath } = useContext(AppDataContext)
  const { isWPA } = useWpaTenant()
  const host = String(isWPA ? process.env.NEXT_PUBLIC_WPA_HOST : process.env.NEXT_PUBLIC_HOST)
  const fixedSlug = asPath.replace(/_/g, '-')
  const canonicalUrl = `${host}/${locale}${fixedSlug}`
  const translate = useTranslateMessage()

  const searchMetaTitlePractice = 'search.meta.title.practice'
  const searchMetaTitleNewPractices = 'search.meta.title.new-practices'
  const searchMetaDescriptionPractice = 'search.meta.description.practice'
  const searchMetaDescriptionNewPractices = 'search.meta.description.new-practices'

  const getTitleKey = (type?: SearchTypes) => {
    const titleKeys: Record<SearchTypes, string> = {
      [SearchTypes.specialists]: 'search.meta.title.specialist',
      [SearchTypes.healthAndSocialCare]: 'search.meta.title.home-care',
      [SearchTypes.practices]: searchMetaTitlePractice,
      [SearchTypes.dentist]: searchMetaTitlePractice,
      [SearchTypes.careHome]: searchMetaTitleNewPractices,
      [SearchTypes.hospital]: searchMetaTitleNewPractices,
      [SearchTypes.pharmacy]: searchMetaTitleNewPractices
    }
    return titleKeys[type!] || searchMetaTitlePractice
  }

  const getDescriptionKey = (type?: SearchTypes) => {
    const descriptionKeys: Record<SearchTypes, string> = {
      [SearchTypes.specialists]: 'search.meta.description.specialist',
      [SearchTypes.healthAndSocialCare]: 'search.meta.description.home-care',
      [SearchTypes.practices]: searchMetaDescriptionPractice,
      [SearchTypes.dentist]: searchMetaDescriptionPractice,
      [SearchTypes.careHome]: searchMetaDescriptionNewPractices,
      [SearchTypes.hospital]: searchMetaDescriptionNewPractices,
      [SearchTypes.pharmacy]: searchMetaDescriptionNewPractices
    }
    return descriptionKeys[type!] || searchMetaDescriptionPractice
  }

  const searchTypeName =
    searchType && newSearchTypesPractices.includes(searchType)
      ? { searchType: translate(getNewPracticesName(searchType)) }
      : {}

  return useMemo(
    () => ({
      title: translate(getTitleKey(searchType), {
        keyword: searchKeywordTitle,
        location: decodeURIComponent(location),
        ...searchTypeName
      }),
      description: translate(getDescriptionKey(searchType), {
        keyword: searchKeywordTitle,
        location: decodeURIComponent(location),
        ...searchTypeName
      }),
      canonicalUrl,
      language,
      noindex: isNoindex
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location, page, searchType, searchKeywordTitle, language, locale]
  )
}
